import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/home';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={slide.image}
          alt={slide.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 md:px-6 w-full flex items-center justify-between">
          {/* Prev arrow */}
          <button
            onClick={prev}
            className="hidden md:flex w-10 h-10 md:w-12 md:h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm text-white transition-all duration-300 cursor-pointer flex-shrink-0"
            aria-label="上一个"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
          </button>

          {/* Text content */}
          <div className="flex-1 text-center px-2 md:px-8" key={slide.title}>
            <div className="inline-flex items-center gap-2 mb-4 fade-in-up" style={{ animationDelay: '0.1s' }}>
              {slide.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-white/15 backdrop-blur-sm text-white border border-white/20"
                >
                  {tag}
                </span>
              ))}
              <span className="text-sm text-white/70">{slide.year}</span>
            </div>

            <h2
              className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              {slide.title}
            </h2>

            <p
              className="text-lg md:text-xl text-white/80 mb-2 fade-in-up"
              style={{ animationDelay: '0.3s' }}
            >
              {slide.subtitle}
            </p>

            <p
              className="text-sm md:text-base text-white/60 max-w-xl mx-auto mb-6 fade-in-up"
              style={{ animationDelay: '0.4s' }}
            >
              {slide.desc}
            </p>

            <div
              className="flex items-center justify-center gap-4 fade-in-up"
              style={{ animationDelay: '0.5s' }}
            >
              <Link
                to={slide.link}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </Link>
              <div className="flex items-center gap-1 text-white/80">
                <i className="ri-star-fill text-accent-500 text-lg"></i>
                <span className="text-sm font-medium">{slide.rating}</span>
              </div>
            </div>
          </div>

          {/* Next arrow */}
          <button
            onClick={next}
            className="hidden md:flex w-10 h-10 md:w-12 md:h-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm text-white transition-all duration-300 cursor-pointer flex-shrink-0"
            aria-label="下一个"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              idx === current
                ? 'w-7 h-2 bg-primary-500'
                : 'w-2 h-2 bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`第${idx + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}